import { useEffect, useMemo } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { PayPalScriptProvider } from '@paypal/react-paypal-js'
import { Elements } from '@stripe/react-stripe-js'
import { StripeElementsOptions } from '@stripe/stripe-js'

import { PageName } from '@/enums/pageName'
import { CheckoutFlowVersion, PaymentPlatform } from '@/enums/payments'
import { StripeWalletButtonTheme } from '@/enums/stripe'
import { getDefaultPayPalOptions } from '@/utils/paypalUtils'
import { getDefaultStripeOptions } from '@/utils/stripeUtils'
import { getPath } from '@/utils/urlUtils'

import { PageTitle } from '@/components/pageTitle'
import { PaymentScreenPolicy } from '@/components/policyLinkBlock'
import { Receipt } from '@/components/receipt'
import { TextBlock } from '@/components/textBlock'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'

import { paypalButtonMap, stripeCheckoutFormMap, walletPaymentButtonMap } from './componentMappings'
import { useProcessContactState } from './hooks/contactState/useProcessContactState'
import useTrackPageAndPaymentScreen from './hooks/trackWebEvents/useTrackWebEvents'
import { TPaymentScreenPageProps } from './types'

const paypalOptions = getDefaultPayPalOptions()

const pageName = PageName.paymentScreen

export const PaymentScreenModal = ({
  getItem,
  setItem,
  pageConfig,
  selectPlanPage,
  config,
  stripePromise,
  stripeFormAppearance,
  paypalButtonConfigStyle,
  getStripeWalletButtonStyles,
  successRedirectTo = PageName.paymentSuccess,
  contactStateRoutesMap,
}: TPaymentScreenPageProps) => {
  const { user } = useAuth()
  const { checkoutFlowVersion } = useSession()
  const {
    discount,
    title,
    pageTitle,
    paymentMethodTitle,
    receiptData,
    receiptTitle,
    stripeFormConfig,
    walletButtonConfig,
    paypalCustomTagline,
    subscriptionsDetails,
  } = pageConfig

  const showPayPal = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.PAYPAL),
    [config]
  )
  const showStripe = useMemo<boolean>(
    () => config.PAYMENT_METHODS.includes(PaymentPlatform.STRIPE),
    [config]
  )

  const selectedPlanId = getItem(PageName.selectPlan)
  const planData = useMemo(
    () => selectPlanPage.plansDetails.find((plan) => plan.planId === selectedPlanId),
    [selectPlanPage.plansDetails, selectedPlanId]
  )
  const navigate = useNavigate()

  useEffect(() => {
    // should be run once
    if (!planData) {
      return navigate(getPath(PageName.selectPlan))
    }
  }, [navigate, planData])

  const stripeOptions = useMemo<StripeElementsOptions | undefined>(
    () => getDefaultStripeOptions(planData, stripeFormAppearance),
    [planData, stripeFormAppearance]
  )

  const className = `page page-${pageName}`

  useTrackPageAndPaymentScreen(config, getItem, setItem, pageTitle, planData, pageName)

  const contactStateEl = useProcessContactState(
    config.PAGE_SET_ID,
    getItem,
    setItem,
    { className, stateRoutes: contactStateRoutesMap },
    checkoutFlowVersion
  )
  if (contactStateEl) {
    return contactStateEl
  }

  if (!user && checkoutFlowVersion !== CheckoutFlowVersion.V2) {
    // TODO: probably there are better typescript hints to avoid this check here
    return <Navigate to={getPath(PageName.registration)} />
  }

  if (!planData || !selectedPlanId) {
    return <Navigate to={getPath(PageName.selectPlan)} />
  }

  if (!stripeOptions) {
    return null
  }

  const WalletPaymentFC = walletPaymentButtonMap[checkoutFlowVersion]
  const PayPalButtonFC = paypalButtonMap[checkoutFlowVersion]
  const StripeCheckoutFormFC = stripeCheckoutFormMap[checkoutFlowVersion]

  return (
    <div className={className}>
      <PayPalScriptProvider options={{ ...paypalOptions }}>
        <Elements stripe={stripePromise} options={stripeOptions}>
          <div className="offer">{title}</div>
          <PageTitle title={discount} />
          <div className="payment-method">{paymentMethodTitle}</div>
          <div className="floating-container ">
            {showStripe && (!walletButtonConfig || !walletButtonConfig.underForm) && (
              <WalletPaymentFC
                planData={planData}
                getItem={getItem}
                setItem={setItem}
                pageName={pageName}
                config={config}
                stripeButtonTheme={StripeWalletButtonTheme.dark}
                getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                successRedirectTo={successRedirectTo}
                contactStateRoutesMap={contactStateRoutesMap}
              />
            )}

            {receiptData && selectedPlanId && (
              <Receipt receiptData={receiptData[selectedPlanId]} receiptTitle={receiptTitle} />
            )}

            <div className="buttons-container">
              {showStripe && walletButtonConfig && walletButtonConfig.underForm && (
                <WalletPaymentFC
                  planData={planData}
                  getItem={getItem}
                  setItem={setItem}
                  pageName={pageName}
                  config={config}
                  stripeButtonTheme={StripeWalletButtonTheme.dark}
                  getStripeWalletButtonStyles={getStripeWalletButtonStyles}
                  showDelimiter={walletButtonConfig.showDelimiter}
                  buttonsDelimiterText={walletButtonConfig.delimiterText}
                  successRedirectTo={successRedirectTo}
                  contactStateRoutesMap={contactStateRoutesMap}
                />
              )}
              {showPayPal && (
                <div className="paypal-container">
                  <PayPalButtonFC
                    pageName={pageName}
                    paypalButtonConfigStyle={paypalButtonConfigStyle}
                    productData={planData}
                    getItem={getItem}
                    setItem={setItem}
                    config={config}
                    customTagline={paypalCustomTagline}
                    successRedirectTo={successRedirectTo}
                    contactStateRoutesMap={contactStateRoutesMap}
                  />
                </div>
              )}
            </div>
            {showStripe && (
              <StripeCheckoutFormFC
                pageName={pageName}
                getItem={getItem}
                setItem={setItem}
                planDetails={planData}
                config={config}
                formConfig={stripeFormConfig}
                successRedirectTo={successRedirectTo}
                contactStateRoutesMap={contactStateRoutesMap}
              />
            )}

            <PaymentScreenPolicy />
            <TextBlock className="subscriptions-details" text={subscriptionsDetails}></TextBlock>
          </div>
        </Elements>
      </PayPalScriptProvider>
    </div>
  )
}
